import React from 'react';
import PropTypes from 'prop-types';
import {Box, Typography, Button, Paper} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import {useJWTAuth} from '../../services/auth/jwt-auth/JWTAuthProvider';

/**
 * PortalRoute - guards service pages for portal users.
 * Non-portal users always pass through.
 * Portal users must have the specified page permission.
 *
 * @param {string}  pageKey  - e.g. 'service.connection'
 * @param {string}  action   - 'view' | 'send' | 'edit'
 * @param {node}    children
 */
const PortalRoute = ({pageKey, action = 'view', children}) => {
  const {user} = useJWTAuth();

  const hasPortalPermission = (key, act = 'view') => {
    if (!user) return false;
    if (user.role !== 'portal_user') return true;
    const perms = user.portalPermissions || [];
    const perm = perms.find((p) => p.page_key === key);
    if (!perm) return false;
    if (act === 'view') return Boolean(perm.can_view);
    if (act === 'send') return Boolean(perm.can_send);
    if (act === 'edit') return Boolean(perm.can_edit);
    return false;
  };

  if (user?.role !== 'portal_user') {
    return children;
  }

  if (!hasPortalPermission(pageKey, action)) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{p: 4, maxWidth: 480, textAlign: 'center', borderRadius: 2}}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <LockIcon sx={{fontSize: 40, color: 'error.main'}} />
          </Box>
          <Typography variant='h5' gutterBottom fontWeight='bold'>
            Access Denied
          </Typography>
          <Typography variant='body1' color='text.secondary' sx={{mb: 2}}>
            You do not have permission to access this page.
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{mb: 3}}>
            Contact the account owner to request access.
          </Typography>
          <Button variant='outlined' onClick={() => window.history.back()}>
            Go Back
          </Button>
        </Paper>
      </Box>
    );
  }

  return children;
};

PortalRoute.propTypes = {
  pageKey: PropTypes.string.isRequired,
  action: PropTypes.oneOf(['view', 'send', 'edit']),
  children: PropTypes.node.isRequired,
};

export default PortalRoute;
